import TAGButton from '@aspendental/shared-components-web/lib/components/TAGButton';
import { Form, MaskedPhoneNumberInput, SingleCheckbox, TextInput, useHookForm } from '@aspendental/shared-forms-web';
import { Box, CircularProgress, Typography } from '@mui/material';
import parse from 'html-react-parser';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { getCheckboxChildStyles, personalInformationStepFormCheckboxesWrapperStyles, personalInformationStepFormConfirmButton, personalInformationStepFormInputsWrapperStyles, personalInformationStepWrapperStyles } from './PersonalInformationStep.styles';
import { personalInformationStepForm, personalInformationStepFormSchema } from './PersonalInformationStepFormData';
import MaxBookingAttemptsDialog from '@src/components/dialogs/maxBookingAttemptsDialog/MaxBookingAttemptsDialog';
import { BRAZE_INFO_SMS_SUBSCRIPTION_GROUP_ID, BRAZE_MARKETING_SMS_SUBSCRIPTION_GROUP_ID, TagTheme, treatmentCodeToReasonCode } from '@src/constants';
import getConfigByBrand from '@src/pages/utils/get-config-by-brand';
import { useReserveAppointment } from '@src/services/useReserveAppointment';
import { useSchedulingStore } from '@src/store';
import { hashValue } from '@src/utils/hashValue';
import { unacceptableInputs } from '@src/utils/offensive-words';
import { pushToDataLayer } from '@src/utils/pushToDataLayer';
type PersonalInformationStepProps = {
  index: number;
};
declare global {
  interface Window {
    Liine: {
      sessionGUID: string;
    };
  }
}
export const PersonalInformationStep = ({
  index
}: PersonalInformationStepProps) => {
  const router = useRouter();
  const {
    currentStepIndex,
    firstName,
    lastName,
    phoneNumber,
    emailAddress,
    receiveTransactionalSMSCheckbox,
    receiveMarketingSMSCheckbox,
    selectedLocation,
    selectedTreatmentId,
    selectedTreatmentName,
    selectedTreatmentCode,
    selectedTimeslot,
    returningGuest,
    setFirstName,
    setLastName,
    setPhoneNumber,
    setEmailAddress,
    setReceiveTransactionalSMSCheckbox,
    setReceiveMarketingSMSCheckbox,
    isUserFlagged
  } = useSchedulingStore(state => state);
  const brand = TagTheme.Chapter;
  const config = getConfigByBrand(brand);
  const [moreInfoNeededError, setMoreInfoNeededError] = useState('');
  const [showMaxBookingAttemptsDialog, setShowMaxBookingAttemptsDialog] = useState(false);
  const {
    mutate: reserveAppointment,
    isPending,
    isSuccess,
    error
  } = useReserveAppointment();
  const isFormDisabled = currentStepIndex !== index;
  const isConfirmVisitButtonDisabled = isFormDisabled || isPending || isSuccess;
  const segmentType = useSchedulingStore().returningGuest ? 1 : 0;
  const form = useHookForm({
    schema: personalInformationStepFormSchema,
    defaultValues: {
      [personalInformationStepForm.firstName.name]: firstName,
      [personalInformationStepForm.lastName.name]: lastName,
      [personalInformationStepForm.phoneNumber.name]: phoneNumber,
      [personalInformationStepForm.emailAddress.name]: emailAddress,
      [personalInformationStepForm.receiveTransactionalSMSCheckbox.name]: receiveTransactionalSMSCheckbox,
      [personalInformationStepForm.receiveMarketingSMSCheckbox.name]: receiveMarketingSMSCheckbox
    }
  });
  const handleSubmit = async (values: FieldValues) => {
    if (returningGuest === undefined || !selectedLocation || !selectedTreatmentId || !selectedTimeslot) {
      setMoreInfoNeededError('Please fill out all required information');
      return;
    }
    setMoreInfoNeededError('');
    const valuesToValidateForOffensiveWords = [{
      name: personalInformationStepForm.firstName.name,
      value: values[personalInformationStepForm.firstName.name]
    }, {
      name: personalInformationStepForm.lastName.name,
      value: values[personalInformationStepForm.lastName.name]
    }, {
      name: personalInformationStepForm.emailAddress.name,
      value: values[personalInformationStepForm.emailAddress.name]
    }];
    const offensiveWordsResult = unacceptableInputs(valuesToValidateForOffensiveWords);
    if (offensiveWordsResult.length > 0) {
      offensiveWordsResult.forEach(name => {
        form.setError(name, {
          type: 'manual',
          message: 'Unknown input'
        });
      });
      return;
    }
    setFirstName(values[personalInformationStepForm.firstName.name]);
    setLastName(values[personalInformationStepForm.lastName.name]);
    setPhoneNumber(values[personalInformationStepForm.phoneNumber.name]);
    setEmailAddress(values[personalInformationStepForm.emailAddress.name]);
    setReceiveTransactionalSMSCheckbox(values[personalInformationStepForm.receiveTransactionalSMSCheckbox.name]);
    setReceiveMarketingSMSCheckbox(values[personalInformationStepForm.receiveMarketingSMSCheckbox.name]);
    const dataForDataLayer = {
      event: 'appointment_step',
      stepName: 'patient_information',
      hashedFirstName: await hashValue(values[personalInformationStepForm.firstName.name]),
      hashedLastName: await hashValue(values[personalInformationStepForm.lastName.name]),
      hashedPhoneNumber: await hashValue(values[personalInformationStepForm.phoneNumber.name]),
      hashedEmailAddress: await hashValue(values[personalInformationStepForm.emailAddress.name]),
      receiveTransactionalSms: values[personalInformationStepForm.receiveTransactionalSMSCheckbox.name],
      receiveMarketingSms: values[personalInformationStepForm.receiveMarketingSMSCheckbox.name],
      treatmentId: selectedTreatmentId,
      treatmentName: selectedTreatmentName,
      reasonCode: treatmentCodeToReasonCode[selectedTreatmentCode],
      segmentType: segmentType
    };
    pushToDataLayer(dataForDataLayer);
    if (isUserFlagged) {
      setShowMaxBookingAttemptsDialog(true);
      fetch(`${process.env.APP_PATH}/api/session-check`, {
        method: 'POST'
      });
    } else {
      reserveAppointment({
        email: values[personalInformationStepForm.emailAddress.name],
        firstName: values[personalInformationStepForm.firstName.name],
        lastName: values[personalInformationStepForm.lastName.name],
        phoneNumber: values[personalInformationStepForm.phoneNumber.name],
        receive_marketing_email: config.receiveEmail,
        receive_transactional_sms: values[personalInformationStepForm.receiveTransactionalSMSCheckbox.name],
        receive_marketing_sms: values[personalInformationStepForm.receiveMarketingSMSCheckbox.name],
        returningGuest,
        selectedLocation: selectedLocation,
        treatmentName: selectedTreatmentName,
        liineSessionGUID: window.Liine.sessionGUID,
        selectedTreatmentId,
        selectedTimeslot,
        reasonCode: treatmentCodeToReasonCode[selectedTreatmentCode]
      }, {
        onSuccess: response => {
          const {
            bookingId,
            guestId
          } = response;
          import('@src/utils/braze-exports').then(({
            getUser,
            User
          }) => {
            getUser()?.setFirstName(firstName);
            getUser()?.setLastName(lastName);
            getUser()?.setEmail(emailAddress);
            getUser()?.setPhoneNumber(phoneNumber);
            getUser()?.setCustomUserAttribute('email_signup_location', 'DCP-Scheduling');
            getUser()?.setEmailNotificationSubscriptionType(User.NotificationSubscriptionTypes.OPTED_IN);
            if (values[personalInformationStepForm.receiveTransactionalSMSCheckbox.name]) {
              getUser()?.setCustomUserAttribute('sms_opt_in_source', 'DCP-Scheduling');
              getUser()?.addToSubscriptionGroup(BRAZE_INFO_SMS_SUBSCRIPTION_GROUP_ID);
            } else {
              getUser()?.removeFromSubscriptionGroup(BRAZE_INFO_SMS_SUBSCRIPTION_GROUP_ID);
            }
            if (values[personalInformationStepForm.receiveMarketingSMSCheckbox.name]) {
              getUser()?.setCustomUserAttribute('sms_opt_in_source', 'DCP-Scheduling');
              getUser()?.addToSubscriptionGroup(BRAZE_MARKETING_SMS_SUBSCRIPTION_GROUP_ID);
            } else {
              getUser()?.removeFromSubscriptionGroup(BRAZE_MARKETING_SMS_SUBSCRIPTION_GROUP_ID);
            }
          });
          pushToDataLayer({
            ...dataForDataLayer,
            stepName: 'appointment_confirmation',
            bookingId,
            guestId
          });
          router.push('thank-you');
        }
      });
    }
  };
  useEffect(() => {
    import('@src/utils/braze-exports').then(({
      getUser
    }) => {
      getUser()?.setFirstName(firstName);
      getUser()?.setLastName(lastName);
      getUser()?.setEmail(emailAddress);
      getUser()?.setPhoneNumber(phoneNumber);
    });
  }, [firstName, lastName, emailAddress, phoneNumber]);
  if (error) {
    return <Typography variant="bodyMediumBook"> Oops! Please refresh the page and try again. </Typography>;
  }
  const termsAndPrivacy = 'See <a href="/terms-of-use/">Terms of Use</a> & <a href="/privacy-policy/">Privacy Policy</a>.';
  return <Box sx={personalInformationStepWrapperStyles} data-sentry-element="Box" data-sentry-component="PersonalInformationStep" data-sentry-source-file="PersonalInformationStep.tsx">
			<Form form={form} onSubmit={handleSubmit} data-sentry-element="Form" data-sentry-source-file="PersonalInformationStep.tsx">
				<Box sx={personalInformationStepFormInputsWrapperStyles} data-sentry-element="Box" data-sentry-source-file="PersonalInformationStep.tsx">
					<TextInput name={personalInformationStepForm.firstName.name} label={personalInformationStepForm.firstName.label} fullWidth disabled={isFormDisabled} autoComplete="on" data-sentry-element="TextInput" data-sentry-source-file="PersonalInformationStep.tsx" />
					<TextInput name={personalInformationStepForm.lastName.name} label={personalInformationStepForm.lastName.label} fullWidth disabled={isFormDisabled} autoComplete="on" data-sentry-element="TextInput" data-sentry-source-file="PersonalInformationStep.tsx" />
					<MaskedPhoneNumberInput name={personalInformationStepForm.phoneNumber.name} label={personalInformationStepForm.phoneNumber.label} fullWidth disabled={isFormDisabled} autoComplete="on" data-sentry-element="MaskedPhoneNumberInput" data-sentry-source-file="PersonalInformationStep.tsx" />
					<TextInput name={personalInformationStepForm.emailAddress.name} label={personalInformationStepForm.emailAddress.label} fullWidth enforceAlphaNumeric={false} disabled={isFormDisabled} autoComplete="on" data-sentry-element="TextInput" data-sentry-source-file="PersonalInformationStep.tsx" />
				</Box>

				<p>{moreInfoNeededError}</p>

				<Box sx={{
        ...personalInformationStepFormCheckboxesWrapperStyles,
        ...getCheckboxChildStyles(isFormDisabled)
      }} data-sentry-element="Box" data-sentry-source-file="PersonalInformationStep.tsx">
					<SingleCheckbox name={personalInformationStepForm.receiveTransactionalSMSCheckbox.name} label={parse(`${personalInformationStepForm.receiveTransactionalSMSCheckbox.label} ${termsAndPrivacy}`) as string} disabled={isFormDisabled} data-sentry-element="SingleCheckbox" data-sentry-source-file="PersonalInformationStep.tsx" />
					<SingleCheckbox name={personalInformationStepForm.receiveMarketingSMSCheckbox.name} label={parse(`${personalInformationStepForm.receiveMarketingSMSCheckbox.label} ${termsAndPrivacy}`) as string} disabled={isFormDisabled} data-sentry-element="SingleCheckbox" data-sentry-source-file="PersonalInformationStep.tsx" />
				</Box>

				<TAGButton disabled={isConfirmVisitButtonDisabled} type="submit" variant="primaryDefault" sx={personalInformationStepFormConfirmButton} size="M" startIcon={isPending && <CircularProgress size="24px" />} data-sentry-element="TAGButton" data-sentry-source-file="PersonalInformationStep.tsx">
					CONFIRM VISIT
				</TAGButton>
			</Form>

			<MaxBookingAttemptsDialog open={showMaxBookingAttemptsDialog} onClose={() => setShowMaxBookingAttemptsDialog(false)} data-sentry-element="MaxBookingAttemptsDialog" data-sentry-source-file="PersonalInformationStep.tsx" />
		</Box>;
};